import { load } from "../../events/load";

/**
 * Checkout Form Handler
 * Handles phone masking, validation, and submit state for the checkout reservation form.
 * Bypasses the reIntegration plugin's submitHandler by using a direct trigger button
 * instead of relying on form submit events.
 */
class CheckoutForm {
    constructor() {
        this.form = document.getElementById("checkout-reservation-form");

        if (!this.form) return;

        // External trigger button in sidebar (order-summary.php)
        this.triggerBtn = document.getElementById("checkout-submit-trigger");
        this.triggerBtnText = this.triggerBtn?.querySelector(".btn__text") || this.triggerBtn;
        this.originalTriggerText = this.triggerBtnText?.textContent || "Зарезервувати";

        // Internal submit button (inside the form from shortcode, may be hidden)
        this.internalBtn = this.form.querySelector('button[type="submit"]');

        this.init();
    }

    init() {
        this.disablePluginHandler();
        this.initPhoneMask();
        this.initValidation();
        this.initTriggerButton();
        this.initSubmitHandler();
    }

    /**
     * Remove form from reIntegration plugin's scope
     * Plugin targets .reintegration-form form — we remove noValidate it set
     */
    disablePluginHandler() {
        // Plugin sets noValidate = true on all .reintegration-form forms
        // We restore native validation selectively (though we handle it ourselves anyway)
        this.form.noValidate = false;

        // Clone and replace the form node to remove ALL existing event listeners
        // (including the plugin's submit handler)
        const formClone = this.form.cloneNode(true);
        this.form.parentNode.replaceChild(formClone, this.form);
        this.form = formClone;

        // Re-query internal button after clone
        this.internalBtn = this.form.querySelector('button[type="submit"]');
    }

    /**
     * Initialize phone input mask (+38 format)
     */
    initPhoneMask() {
        const phoneInput = this.form.querySelector('input[name="phone"]');
        if (!phoneInput) return;

        this.phoneInput = phoneInput;

        // Remove data-mask to prevent plugin IMask conflict
        phoneInput.removeAttribute("data-mask");

        // Destroy IMask instance if already attached
        if (phoneInput._imask) {
            phoneInput._imask.destroy();
        }

        // Set initial value
        if (!phoneInput.value || phoneInput.value.length < 3) {
            phoneInput.value = "+38";
        }

        phoneInput.addEventListener("input", (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, "");

            if (!value.startsWith("38")) {
                value = "38" + value;
            }

            value = value.substring(0, 12);

            let formatted = "+";
            if (value.length > 0) formatted += value.substring(0, 2);
            if (value.length > 2) formatted += ` (${value.substring(2, 5)}`;
            if (value.length > 5) formatted += `) ${value.substring(5, 8)}`;
            if (value.length > 8) formatted += `-${value.substring(8, 10)}`;
            if (value.length > 10) formatted += `-${value.substring(10, 12)}`;

            input.value = formatted;
        });

        phoneInput.addEventListener("keydown", (e) => {
            const cursorPos = e.target.selectionStart;
            if (cursorPos <= 3 && (e.key === "Backspace" || e.key === "Delete")) {
                e.preventDefault();
            }
        });

        phoneInput.addEventListener("focus", (e) => {
            if (!e.target.value || e.target.value.length < 3) {
                e.target.value = "+38";
            }
            setTimeout(() => {
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
            }, 0);
        });
    }

    /**
     * Initialize form validation (clear invalid state on input)
     */
    initValidation() {
        const inputs = this.form.querySelectorAll(".checkout-form__input");
        inputs.forEach((input) => {
            input.addEventListener("input", () => {
                input.classList.remove("is-invalid");
            });
        });
    }

    /**
     * Connect the sidebar trigger button to the checkout form
     */
    initTriggerButton() {
        if (!this.triggerBtn) return;

        this.triggerBtn.addEventListener("click", () => {
            this.handleCheckout();
        });

        // Also handle the internal form submit button (if visible/clickable)
        if (this.internalBtn) {
            this.internalBtn.addEventListener("click", (e) => {
                e.preventDefault();
                this.handleCheckout();
            });
        }
    }

    /**
     * Prevent the default form submit (plugin leftover or accidental)
     */
    initSubmitHandler() {
        this.form.addEventListener("submit", (e) => {
            e.preventDefault();
        });
    }

    /**
     * Main checkout logic — validate, collect data, send AJAX
     */
    async handleCheckout() {
        this.clearErrors();
        if (!this.validateForm()) return;

        this.setLoadingState(true);

        const formData = new FormData(this.form);
        formData.append("action", "liora_checkout_reservation");
        formData.append("checkout_nonce", window.params.checkout_nonce);

        try {
            const response = await fetch(window.params.ajax_url, {
                method: "POST",
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                document.dispatchEvent(
                    new CustomEvent("reintegration:success", { detail: data })
                );

                if (data.data?.redirect_url) {
                    window.location.href = data.data.redirect_url;
                } else {
                    alert(
                        window.params?.i18n?.form_success_title || "Дякуємо!"
                    );
                    this.form.reset();
                    this.setLoadingState(false);
                }
            } else {
                const responseData = data.data || data;
                const errorMessage = responseData?.message || "Помилка при відправці форми";

                if (responseData?.field) {
                    this.showFieldError(responseData.field, errorMessage);
                } else {
                    alert(errorMessage);
                }
                this.setLoadingState(false);
            }
        } catch (error) {
            console.error("Checkout Error:", error);
            alert(
                window.params?.i18n?.error_connection || "Помилка з'єднання"
            );
            this.setLoadingState(false);
        }
    }

    /**
     * Validate all required fields
     */
    validateForm() {
        this.clearErrors();
        const inputs = this.form.querySelectorAll("[required]");
        let isValid = true;
        let firstInvalid = null;

        const i18n = window.params?.i18n || {};

        inputs.forEach((input) => {
            const value = input.value.trim();
            const fieldName = (input.name || input.id || "").toLowerCase();

            // 1. Length Check
            if (value.length > 500) {
                isValid = false;
                this.showFieldError(input.name || input.id, i18n.form_error_too_long || "Too many characters");
                if (!firstInvalid) firstInvalid = input;
                return;
            }

            // 2. Phone Check
            if (input.name === "phone" || fieldName.includes("phone")) {
                const digits = value.replace(/\D/g, "");
                if (digits.length !== 12) {
                    isValid = false;
                    this.showFieldError(input.name || input.id, i18n.phone_error || "Invalid phone number");
                    if (!firstInvalid) firstInvalid = input;
                }
                return;
            }

            // 3. Name/Surname Check (No digits, No symbols)
            const isNameField = fieldName.includes("імʼя") || fieldName.includes("імя") || fieldName.includes("name") || fieldName.includes("surname") || fieldName.includes("прізвище") || fieldName.includes("first_name") || fieldName.includes("last_name");

            if (isNameField && value) {
                if (value.length > 50) {
                    isValid = false;
                    this.showFieldError(input.name || input.id, i18n.form_error_name_too_long || "Name too long");
                    if (!firstInvalid) firstInvalid = input;
                    return;
                }

                // Strictly no digits and minimal punctuation for names
                const nameRegex = /^[\p{L}\s\-\.]+$/u;
                if (!nameRegex.test(value)) {
                    isValid = false;
                    this.showFieldError(input.name || input.id, i18n.form_error_invalid_chars || "Invalid characters");
                    if (!firstInvalid) firstInvalid = input;
                    return;
                }
            }

            // 4. Global Emoji Check
            const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
            if (emojiRegex.test(value)) {
                isValid = false;
                this.showFieldError(input.name || input.id, i18n.form_error_contains_emojis || "Emojis not allowed");
                if (!firstInvalid) firstInvalid = input;
                return;
            }

            // 5. Empty Check
            if (!value) {
                isValid = false;
                input.classList.add("is-invalid");
                if (!firstInvalid) firstInvalid = input;
            }
        });

        if (!isValid && firstInvalid) {
            firstInvalid.focus();
        }

        return isValid;
    }

    /**
     * Toggle loading state on the trigger button
     */
    setLoadingState(isLoading) {
        if (!this.triggerBtn) return;

        if (isLoading) {
            this.triggerBtn.disabled = true;
            this.triggerBtnText.textContent =
                window.params?.i18n?.loading || "резервування...";
        } else {
            this.triggerBtn.disabled = false;
            this.triggerBtnText.textContent = this.originalTriggerText;
        }
    }

    showFieldError(fieldName, message) {
        const input = this.form.querySelector(`[name="${fieldName}"]`) ||
            this.form.querySelector(`#${fieldName}`) ||
            this.form.querySelector(`[id*="${fieldName}"]`);

        if (!input) {
            alert(message);
            return;
        }

        input.classList.add("is-invalid");

        let errorElement = input.parentNode.querySelector(".contact-form__error");
        if (!errorElement) {
            errorElement = document.createElement("span");
            errorElement.className = "contact-form__error";
            input.parentNode.appendChild(errorElement);
        }

        errorElement.textContent = message;
        errorElement.classList.add("is-visible");

        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        input.focus();
    }

    clearErrors() {
        this.form.querySelectorAll(".is-invalid").forEach(el => el.classList.remove("is-invalid"));
        this.form.querySelectorAll(".contact-form__error").forEach(el => el.classList.remove("is-visible"));
    }
}

load(() => {
    new CheckoutForm();
});
